/**
 * The real analytics transport (spec sections 14, 16).
 *
 * `lib/analytics.ts` defines `track()` and a transport seam; this is what gets
 * installed into that seam at bootstrap. Nothing that calls `track()` knows
 * this file exists, which is the point of the seam.
 *
 * Three constraints, in order of importance:
 *
 * 1. **It must never block the interaction it is measuring.** `cta_clicked`
 *    fires on a link that is about to navigate and `owner_signup_completed`
 *    fires immediately before a route change. `navigator.sendBeacon` is built
 *    for exactly this: the browser takes ownership of the request and completes
 *    it after the page is gone. `fetch(..., { keepalive: true })` is the
 *    fallback for browsers without it. Neither is awaited.
 * 2. **It must never throw.** `track()` already swallows, but a transport that
 *    throws on every call would fill a visitor's console for no reason.
 * 3. **It must not send anything the server would reject.** The payload is
 *    validated against the *same* schema the endpoint validates with
 *    (`shared/schemas/events.ts`) before it goes anywhere. A property key that
 *    is not on the allow list is dropped here rather than 400-ing there, and in
 *    development it says so loudly — that mismatch is a bug in a `track()` call
 *    site, and it should be found in a test rather than in a month of missing
 *    analytics.
 *
 * The session id is the one from the attribution record, so an event and the
 * signup it preceded can be joined without either carrying anything about the
 * person (spec section 16).
 */

import { eventSchema } from '../../shared/schemas/events';
import { setAnalyticsTransport, type AnalyticsEventName, type AnalyticsProps } from './analytics';
import { getAttribution } from './attribution';

const ENDPOINT = '/api/events';

/**
 * The landing A/B assignment, added to every event's properties here rather
 * than at each `track()` call site. One place to attach it means the whole
 * funnel carries the dimension, including the events added after the test
 * started, and no call site can forget it.
 *
 * Dropped when the visitor has no variant on their record, in keeping with the
 * rest of this file: a missing dimension is an unsplit event, not a lost one.
 */
function withVariant(props: AnalyticsProps | undefined): AnalyticsProps | undefined {
  const { variant } = getAttribution();
  if (variant === undefined) return props;
  return { ...props, variant };
}

/**
 * Builds the wire payload, or null if it would not validate. Returning null is
 * the safe direction: an event is worth less than a console error on a page a
 * horse owner is trying to use.
 */
function payloadFor(event: AnalyticsEventName, props: AnalyticsProps | undefined): string | null {
  const properties = withVariant(props);
  const candidate = {
    eventName: event,
    path: window.location.pathname,
    sessionId: getAttribution().sessionId,
    properties: properties && Object.keys(properties).length > 0 ? properties : undefined,
  };

  const parsed = eventSchema.safeParse(candidate);
  if (!parsed.success) {
    if (import.meta.env.DEV) {
      console.warn(`[analytics] ${event} does not match the event schema and was not sent`);
    }
    return null;
  }
  return JSON.stringify(parsed.data);
}

/**
 * Hands the request to the browser and returns immediately.
 *
 * The beacon body is a `Blob` with an explicit JSON content type: a bare string
 * is sent as `text/plain`, which `express.json()` ignores, and the event would
 * be silently discarded at the other end.
 */
function dispatch(body: string): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      // Returns false when the payload is over the browser's queue limit.
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }

    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      // Survives the page being torn down mid-navigation.
      keepalive: true,
      credentials: 'same-origin',
    }).catch(() => {
      // Analytics is best effort by design.
    });
  } catch {
    // A blocked beacon, a CSP rejection, a browser extension: none of it is
    // the visitor's problem.
  }
}

/**
 * Installs the transport. Called once, from bootstrap.
 *
 * Development keeps the console line as well as the send, so a placement can
 * still be checked in a browser without opening the network tab.
 */
export function installAnalyticsTransport(): void {
  setAnalyticsTransport((event, props) => {
    if (import.meta.env.DEV) {
      console.info(`[analytics] ${event}`, props ?? {});
    }
    const body = payloadFor(event, props);
    if (body !== null) dispatch(body);
  });
}
